import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import RichTextEditor from './RichTextEditor';
import { hasActualTextContent } from '../utils/sanitize';

export type ChangeApprovalDecision = 'approved' | 'rejected' | 'skipped';

interface ChangeApprovalDecisionModalProps {
  isOpen: boolean;
  decision: ChangeApprovalDecision;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (comments: string) => void | Promise<void>;
  /** When true, reject requires non-empty comments (Change Request default). */
  requireCommentsOnReject?: boolean;
  /** Shown in skip modal header context. */
  changeNumber?: string | null;
}

/**
 * Shared Approve / Reject / Skip Change modal matching Change Request detail UI.
 * Comments are optional for Approve and Skip; optional required for Reject via prop.
 */
const ChangeApprovalDecisionModal: React.FC<ChangeApprovalDecisionModalProps> = ({
  isOpen,
  decision,
  isSubmitting = false,
  onClose,
  onSubmit,
  requireCommentsOnReject = false,
  changeNumber,
}) => {
  const [comments, setComments] = useState('');

  useEffect(() => {
    if (isOpen) setComments('');
  }, [isOpen, decision]);

  const isApprove = decision === 'approved';
  const isSkip = decision === 'skipped';
  const title = isApprove
    ? 'Approve Change'
    : isSkip
      ? 'Skip Change'
      : 'Reject Change';
  const label = isApprove
    ? 'Comments (Optional)'
    : isSkip
      ? 'Comments (Optional)'
      : 'Rejection Reason';
  const placeholder = isApprove
    ? 'Add any approval comments...'
    : isSkip
      ? 'Add any skip comments...'
      : 'Please provide a reason for rejection...';
  const rejectBlocked =
    !isApprove && !isSkip && requireCommentsOnReject && !hasActualTextContent(comments);
  const submitDisabled = isSubmitting || rejectBlocked;
  const submitLabel = isApprove ? 'Approve' : isSkip ? 'Skip CR' : 'Reject';
  const submitClass = isApprove
    ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
    : isSkip
      ? 'bg-teal-700 hover:bg-teal-800 focus:ring-teal-500'
      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500';

  const handleSubmit = async () => {
    if (submitDisabled) return;
    await onSubmit(comments);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !isSubmitting && onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between"
                >
                  {title}
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                    onClick={onClose}
                    disabled={isSubmitting}
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </Dialog.Title>

                {isSkip && (
                  <div className="mt-3 space-y-2 text-sm text-gray-700">
                    {changeNumber ? (
                      <p>
                        <span className="font-medium text-gray-900">Change Request:</span>{' '}
                        {changeNumber}
                      </p>
                    ) : null}
                    <p className="text-gray-600">
                      This Change Request will be moved to the end of the current agenda and can
                      be revisited later.
                    </p>
                  </div>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {!isApprove && !isSkip && requireCommentsOnReject && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>
                  <RichTextEditor
                    value={comments}
                    onChange={setComments}
                    placeholder={placeholder}
                    minHeight="120px"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${submitClass}`}
                    disabled={submitDisabled}
                    onClick={handleSubmit}
                  >
                    {isSubmitting ? 'Processing...' : submitLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ChangeApprovalDecisionModal;
